import FormatUtils from '../../utils/FormatUtils';
import { IColInfo, IColumnUserSettings, IFilterInfo, ISortInfo } from './Interfaces';

export class TableDataProcessor {
    colInfos: IColInfo[]
    data: any[]
    sortInfos: ISortInfo[]
    filterInfos: IFilterInfo[]
    colUserSettings: IColumnUserSettings[]
    processedData: any[]
    formatUtils: FormatUtils = new FormatUtils();

    constructor() {
        this.colInfos = [];
        this.data = [];
        this.sortInfos = [];
        this.filterInfos = [];
        this.colUserSettings = []
        this.processedData = [];
    }

    init() {
        if (this.data.length > 0 && this.colInfos.length > 0) {
            this.sortInfos = this.getSortInfos(this.colInfos, this.colUserSettings);
            this.filterInfos = this.getFilterInfos(this.colInfos, this.colUserSettings);
            this.getFilterOptions(this.filterInfos)
        }
        this.processedData = [];
    }

    getSortInfos(colInfos: IColInfo[], colUserSettings: IColumnUserSettings[]) {
        let sortInfos: ISortInfo[] = [];
        for (var i = 0; i < colInfos.length; i++) {
            if (colUserSettings[i].sort === "asc" || colUserSettings[i].sort === "desc") {
                sortInfos.push({
                    colInfo: colInfos[i],
                    colUserSettings: colUserSettings[i],
                    sortType: (colInfos[i].dataType ? colInfos[i].dataType : 'string') + '_' + colUserSettings[i].sort,
                    prio: colUserSettings[i].sortPrio ?? 999
                });
            }
        };

        sortInfos.sort((a, b) => { return a.prio < b.prio ? -1 : ((a.prio > b.prio) ? 1 : 0); })
        return sortInfos;
    }

    getFilterInfos(colInfos: IColInfo[], colUserSettings: IColumnUserSettings[]) {
        let filterInfos: IFilterInfo[] = [];
        for (var i = 0; i < colInfos.length; i++) {
            const colInfo = colInfos[i]
            if (colInfo.filterInfo) {
                var filterInfo: IFilterInfo = {
                    colInfo: colInfo,
                    colUserSettings: colUserSettings[i]
                }
                filterInfos.push(filterInfo);
            }
        }
        return filterInfos;
    }

    setColInfos(colInfos: IColInfo[], colUserSettings: IColumnUserSettings[]) {
        this.colInfos = colInfos;
        this.colUserSettings = colUserSettings
        this.init();
    }

    setData(data: any[]) {
        this.data = data;
        this.init();
    }

    process() {
        const filteredData = this.filter(this.data);
        this.processedData = this.sort(filteredData);
    }

    filter(tableData: any[]): any[] {
        return tableData.filter((rowData) => this.filterRow(rowData, this.filterInfos))
    }

    filterRow(rowData: any, filterInfos: IFilterInfo[]): boolean {
        let result = true
        if (filterInfos !== undefined) {
            filterInfos.forEach((filterInfo) => {
                let value = this.getValueOf(rowData, filterInfo.colInfo)
                if (this.isInFilter(value, filterInfo) === false)
                    result = false;
            })
        }
        return result;
    }

    isInFilter(value: any, filterInfo: IFilterInfo): boolean {
        if (filterInfo.colUserSettings.selectedFilterValues !== undefined) {
            if (filterInfo.colUserSettings.selectedFilterValues.length === 0) {
                return true;
            }

            let result = false;
            filterInfo.colUserSettings.selectedFilterValues.forEach((filterValue) => {
                switch (filterInfo.colInfo.filterInfo?.filterType) {
                    case 'CheckboxList':
                        if (value === filterValue) {
                            result = true
                        }
                        break
                    case 'Textbox':
                        if (filterValue === '')
                            result = true

                        switch (filterInfo.colInfo.dataType) {
                            case 'number':
                                if (value == filterValue)
                                    result = true
                                break
                            default:
                                filterValue.split(' ').forEach((filterVal) => { 
                                    if (value.toLowerCase().includes(filterVal.toLowerCase()))
                                        result = true
                                })
                                break
                        }
                        break
                }
            })
            return result

        }
        return true
    }

    sort(tableData: any[]):any[] {
        if (this.sortInfos.length === 0)
            return tableData;

        tableData.sort((rowA, rowB) => {
            let result = 0;

            for (let i = 0; i < this.sortInfos.length; i++) {
                const sortInfo = this.sortInfos[i];

                let valueA = this.getValueOf(rowA, sortInfo.colInfo);
                let valueB = this.getValueOf(rowB, sortInfo.colInfo);

                switch (sortInfo.sortType) {
                    case "date_desc":
                    case "datetime_desc":
                        if (valueA === '' && valueB === '')
                            return 0;
                        else if (valueA === '')
                            result = -1;
                        else if (valueB === '')
                            result = 1;
                        else
                            result = valueB.toString().localeCompare(valueA);
                        break;

                    case "date_asc":
                    case "datetime_asc":
                        if (valueA === '' && valueB === '')
                            return 0;
                        else if (valueA === '')
                            result = 1;
                        else if (valueB === '')
                            result = -1;
                        else
                            result = valueA.toString().localeCompare(valueB);
                        break;

                    case "thumbnail_desc":
                    case "user_desc":
                    case "item_desc":
                    case "itemrevision_desc":
                    case "userDefinedField_desc":
                    case "string_desc":
                        if (valueB != null)
                            result = valueB.toString().localeCompare(valueA);
                        else if (valueA != null)
                            result = -1;
                        else
                            result = 0;
                        break;

                    case "project_desc":
                    case "bool_desc":
                    case "number_desc":
                    case "decimal_desc":
                    case "percentage_desc":
                    case "money_desc":
                        result = valueA > valueB ? -1 : ((valueA < valueB) ? 1 : 0);
                        break;

                    case "thumbnail_asc":
                    case "user_asc":
                    case "item_asc":
                    case "itemrevision_asc":
                    case "userDefinedField_asc":
                    case "string_asc":
                        if (valueA != null)
                            result = valueA.toString().localeCompare(valueB);
                        else if (valueB != null)
                            result = -1;
                        else
                            result = 0;
                        break;

                    case "project_asc":
                    case "bool_asc":
                    case "number_asc":
                    case "decimal_asc":
                    case "percentage_asc":
                    case "money_asc":
                        result = valueA < valueB ? -1 : ((valueA > valueB) ? 1 : 0);
                        break;
                }

                if (result !== 0) {
                    return result;
                }
            }

            return result;
        });

        return tableData;
    }

    getValueOf(rowData: any, colInfo: IColInfo) {
        let value: any = null;
        if (colInfo.sortProp)
            value = colInfo.sortProp.split('.').reduce((o, i) => (o === null || o === undefined) ? null : o[i], rowData);

        else {
            if (colInfo.getCellData)
                value = colInfo.getCellData(rowData, colInfo);
            else if (colInfo.dataProp)
                value = colInfo.dataProp.split('.').reduce((o, i) => (o === null || o === undefined) ? null : o[i], rowData);

            if (value == null)
                value = '';
            else if (colInfo.dataType === 'user' || colInfo.dataType === 'thumbnail')
                value = value.name;
            else if (colInfo.dataType === 'project')
                value = value.id;
            else if (colInfo.dataType === 'brand')
                value = value.nameTree;
            else if (colInfo.dataType === 'item')
                value = value.itemNr;
            else if (colInfo.dataType === 'itemrevision')
                value = value.item.itemNr + ' - v' + value.revisionNumber;
        }


        return value;
    }

    getFilteredAndSortedData() {
        return this.processedData;
    }

    getProcessedData(start: number, end: number) {
        return this.processedData.slice(start, end);
    }

    getFilterOptions(filterInfos: IFilterInfo[]) {
        if (this.data.length > 0) {
            filterInfos.forEach((filterInfo) => {
                if (filterInfo.colInfo.filterInfo?.filterType === 'CheckboxList') {
                    filterInfo.colInfo.filterInfo.filterOptions = this.data
                        .map((rowData) => { return this.getValueOf(rowData, filterInfo.colInfo) })
                        .filter((value, index, array) => array.indexOf(value) === index) //distinct
                        .sort((a, b) => {
                            if (a < b)
                                return -1;
                            if (a > b)
                                return 1;
                            return 0;
                        })
                        .map((option) => {
                            let label = option
                            switch (filterInfo.colInfo.dataType) {
                                case 'bool':
                                    if (option !== undefined && option !== null)
                                        label = this.formatUtils.formatBoolean(option, filterInfo.colInfo.trueValue ? filterInfo.colInfo.trueValue : 'Yes', filterInfo.colInfo.falseValue ? filterInfo.colInfo.falseValue : 'No');
                                    break;
                                case 'date':
                                    label = this.formatUtils.formatDate(option);
                                    break
                                case 'datetime':
                                    label = this.formatUtils.formatDateTime(option);
                                    break
                            }
                            return { value: option, label: label }
                        });
                }
            })
        }
    }
}